import config from '@/config';
import type { TaskCompletionApiRow } from '@/utils/learningCenterTasksMerge';
import { isTeacherPreviewIdentityId } from '@/utils/toolSettingsUtils';
import {
  readTasksSnapshotJson,
  writeTasksSnapshotFromJson,
  getTasksSnapshotSessionWriteSeq,
} from '@/utils/tasksSnapshotSession';

export type { TaskCompletionApiRow };
export type { ApiSubtaskCompletionRow } from '@/utils/taskCompletionMerge';
export type { ApiSubtaskAutocompleteRow } from '@/utils/onboardingSubtasksMergeFromApi';

/** Gateways sometimes return HTTP 200 with `{ errorMessage, errorCause }` instead of a list. */
function assertTaskApiJsonNotErrorEnvelope(data: unknown, context: string): void {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    if (typeof o.errorMessage === 'string' || typeof o.errorCause === 'string') {
      const msg = [o.errorMessage, o.errorCause].filter(Boolean).join(' — ');
      throw new Error(`${context}: ${msg || 'unknown error'}`);
    }
  }
}

function relayMutationFailMessage(raw: unknown, fallback: string): string {
  let msg = fallback;
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (typeof o.errorMessage === 'string') msg = o.errorMessage;
    else if (typeof o.title === 'string' && Array.isArray(o.violations)) {
      const v = (o.violations as { message?: string }[])
        .map((x) => x?.message)
        .filter(Boolean)
        .join('; ');
      msg = v || String(o.title);
    }
  }
  return msg;
}

type TasksCompleteRelayBody = {
  identityId: number;
  taskId: number;
  type: 'task' | 'subtask';
  completed: boolean;
  userIdentityId: number;
  userAccountId: number;
};

function studentIdentityAsNumber(studentIdentityId: string): number {
  const n = Number(studentIdentityId);
  if (!Number.isFinite(n)) {
    throw new Error(`Invalid identityId for tasks/complete: ${studentIdentityId}`);
  }
  return n;
}

/**
 * PUT unified `/tasks/complete` relay; JSON body carries student `identityId` and completion fields per relay API.
 * Does **not** GET `/tasks` afterward — callers refresh the session snapshot when appropriate (page load, Mark Complete).
 */
async function mutateTasksCompleteRelay(
  accessToken: string,
  studentIdentityId: string,
  body: TasksCompleteRelayBody
): Promise<void> {
  const url = `${config.api.taskCompletionBaseUrl}/tasks/complete`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const raw: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      `Failed to update task completion (${body.type}): ${res.status} ${relayMutationFailMessage(raw, res.statusText)}`
    );
  }
  assertTaskApiJsonNotErrorEnvelope(raw, 'tasks complete relay');
}

/** Coalesce concurrent GET `/tasks` for the same student + token (e.g. TasklistProvider + tasklist page). */
const tasksSnapshotInflight = new Map<string, Promise<unknown>>();

/**
 * GET `/tasks?identityId={identityId}` — full relay payload persisted to session storage per student.
 * Concurrent callers share one network request. If the session snapshot was updated locally while this
 * request was in flight, the response is **not** written to session storage (avoids clobbering subtask patches).
 */
export async function fetchTasksSnapshotAndPersist(
  accessToken: string,
  identityId: string
): Promise<unknown> {
  if (isTeacherPreviewIdentityId(identityId)) {
    return readTasksSnapshotJson(identityId) ?? null;
  }

  const dedupeKey = `${identityId}::${accessToken}`;
  const existing = tasksSnapshotInflight.get(dedupeKey);
  if (existing) return existing;

  const promise = (async () => {
    const sessionWriteTokenAtStart = getTasksSnapshotSessionWriteSeq();
    const url = config.api.endpoints.TASKS(identityId);
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch tasks snapshot: ${res.status} ${res.statusText}`);
    }
    const data: unknown = await res.json();
    assertTaskApiJsonNotErrorEnvelope(data, 'tasks snapshot');
    if (getTasksSnapshotSessionWriteSeq() !== sessionWriteTokenAtStart) {
      return readTasksSnapshotJson(identityId) ?? data;
    }
    writeTasksSnapshotFromJson(identityId, data);
    return data;
  })().finally(() => {
    tasksSnapshotInflight.delete(dedupeKey);
  });

  tasksSnapshotInflight.set(dedupeKey, promise);
  return promise;
}

function normalizeTasksPayloadToList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const inner = o.tasks ?? o.taskList ?? o.data ?? o.results ?? o.items;
    if (inner !== undefined) return normalizeTasksPayloadToList(inner);
  }
  return [];
}

/** Parent task rows used for Overview / LC merge; skips `type: "subtask"` children from GET `/tasks`. */
function parseTaskCompletionRows(data: unknown): TaskCompletionApiRow[] {
  const list = normalizeTasksPayloadToList(data);
  const out: TaskCompletionApiRow[] = [];
  for (const row of list) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const rowType = String(o.type ?? '').trim().toLowerCase();
    if (rowType === 'subtask') continue;
    const rawId = o.taskId ?? o.id;
    const taskId = Number(rawId);
    if (!Number.isFinite(taskId)) continue;
    const completed =
      typeof o.completed === 'boolean'
        ? o.completed
        : String(o.completed).toLowerCase() === 'true' ||
          String(o.status ?? '').toUpperCase() === 'COMPLETE';
    out.push({ taskId, completed: Boolean(completed) });
  }
  return out;
}

/** Parse parent task completion rows from an in-memory GET `/tasks` payload (session snapshot or network). */
export function parseTaskCompletionRowsFromTasksPayload(data: unknown): TaskCompletionApiRow[] {
  return parseTaskCompletionRows(data);
}

/** GET `/tasks?identityId=…` → same network + session path as {@link fetchTasksSnapshotAndPersist}, then parse parents. */
export async function fetchTasksAutocomplete(
  accessToken: string,
  identityId: string
): Promise<TaskCompletionApiRow[]> {
  const snapshot = await fetchTasksSnapshotAndPersist(accessToken, identityId);
  return parseTaskCompletionRowsFromTasksPayload(snapshot);
}

/** Logged-in user completing (or toggling) a subtask on behalf of `studentIdentityId`. */
export type SubtaskCompleteActor = {
  userIdentityId: number;
  userAccountId: number;
};

/**
 * Unified relay `PUT …/tasks/complete`: for **`type: subtask`**, `taskId` is the **subtask row id** from GET `/tasks`
 * (`taskId` + `type: "subtask"` in response). Parent id is unused by the relay but kept for callers.
 */
export async function putSubtaskComplete(
  accessToken: string,
  studentIdentityId: string,
  _relayParentTaskId: number,
  subTaskId: number,
  complete: boolean,
  actor: SubtaskCompleteActor
): Promise<void> {
  await mutateTasksCompleteRelay(accessToken, studentIdentityId, {
    identityId: studentIdentityAsNumber(studentIdentityId),
    taskId: subTaskId,
    type: 'subtask',
    completed: complete,
    userIdentityId: actor.userIdentityId,
    userAccountId: actor.userAccountId,
  });
}

/** Unified relay `PUT …/tasks/complete`: `taskId` is the parent **`type: task`** row from GET `/tasks`. */
export async function putTaskComplete(
  accessToken: string,
  studentIdentityId: string,
  taskId: number,
  complete: boolean,
  actor: SubtaskCompleteActor
): Promise<void> {
  await mutateTasksCompleteRelay(accessToken, studentIdentityId, {
    identityId: studentIdentityAsNumber(studentIdentityId),
    taskId,
    type: 'task',
    completed: complete,
    userIdentityId: actor.userIdentityId,
    userAccountId: actor.userAccountId,
  });
}
